import * as moment from "moment";
import { CalendarObjectType, CalendarViewMode } from "../enumerations";
import { ICalendar, ICalendarGroup } from "../models/get";
import { ISelectedObject, ISelectedObjectId, ISelectedObjectReturn } from "../models/calendar";
import { IQueryString, INewLocation } from "../models/environment";
import { parseValue } from "../utils/utils";
import { getSelectedObjectAsync } from "../utils/calendar";
import { getViewModeFromString, getViewModeString } from "../utils/viewmode";
import { getCalendar } from "../api/calendars";

declare global
{
    namespace uri
    {
        interface URI
        {
            // ALT
            getObjectId(): number;
            getMetadata(): any;

            // NEU
            isRoot: () => boolean,
            hrefRelative: () => string,
            getObject: () => ISelectedObjectId,
            setObject: (type: CalendarObjectType | string, id: number) => void,
            getObjectAsync: () => Promise<ISelectedObjectReturn>,
            getSelectedDate: () => moment.Moment;
            getSelectedCalendarId: () => number;
            getSelectedCalendarAsync: () => Promise<ICalendar>;
            getViewMode: () => CalendarViewMode,
            getViewModeString: () => string,
            getQueryParameterValue<T>(key: string): T,
            getQueryString: () => IQueryString,
            setNewLocation: (options: INewLocation) => uri.URI
        }
    }
}

URI.prototype.isRoot = function (): boolean
{
    var uri: uri.URI = this;
    return uri.pathname() === "/";
};

URI.prototype.hrefRelative = function(): string
{
    var uri: uri.URI = this;
    return uri.pathname() + uri.search();
};

URI.prototype.getObject = function(): ISelectedObjectId
{
    var uri: uri.URI = this;
    var type: string = uri.segment()[0];
    var id: number = parseValue<number>(uri.segment()[1]);
    var returnType: CalendarObjectType;

    switch (type)
    {
        case CalendarObjectType.all:
            returnType = CalendarObjectType.all
            break;
        case CalendarObjectType.calendar:
            returnType = CalendarObjectType.calendar
            break;
        case CalendarObjectType.calendarGroup:
            returnType = CalendarObjectType.calendarGroup
            break;
        default:
            returnType = CalendarObjectType.calendar
            break;
    }

    return {
        type: returnType,
        id: id
    };
};

URI.prototype.setObject = function(type: CalendarObjectType | string, id: number): void
{
    var uri: uri.URI = this;
    uri.segment(0, type);
    uri.segment(1, id.toString());
};

URI.prototype.getObjectAsync = async function(): Promise<ISelectedObjectReturn>
{
    var uri: uri.URI = this;
    var type: string = uri.segment()[0];
    var id: number = parseValue<number>(uri.segment()[1]);

    return await getSelectedObjectAsync({
        type: ((type === CalendarObjectType.calendar) ? CalendarObjectType.calendar : CalendarObjectType.calendarGroup),
        id: id
    });
};

URI.prototype.getSelectedDate = function(): moment.Moment
{
    var uri: uri.URI = this;
    var segments: string[] = uri.segment();

    var year = parseValue<number>(uri.segment()[2]);
    var month = parseValue<number>(uri.segment()[3]);
    var day = parseValue<number>((segments.length === 5 ? segments[4] : "0"));

    if (day != null && day > 0)
    {
        return moment([year, month - 1, day]);
    }
    else
    {
        return moment([year, month - 1, 1]);
    }
};

URI.prototype.getSelectedCalendarId = function (): number
{
    var uri: uri.URI = this;
    var id: number = uri.getQueryParameterValue<number>("selected");
    return id;
};

URI.prototype.getSelectedCalendarAsync = async function(): Promise<ICalendar>
{
    var uri: uri.URI = this;
    var id: number = uri.getQueryParameterValue<number>("selected");

    if (id != null && id > 0)
        return await getCalendar(id);
    else
        return null;
};

URI.prototype.getViewMode = function(): CalendarViewMode
{
    
    var uri: uri.URI = this;
    var viewMode: string = uri.query(true)["viewMode"];
    return getViewModeFromString(viewMode);
};

URI.prototype.getViewModeString = function(): string
{
    var uri: uri.URI = this;
    return uri.query(true)["viewMode"];
};

URI.prototype.getQueryParameterValue = function<T>(key: string): T
{
    return parseValue<T>(this.query(true)[key]);
};

URI.prototype.getQueryString = function(): IQueryString
{
    var uri: uri.URI = this;
    var query = uri.search(true);

    return {
        selected: parseValue<number>(query.selected),
        viewMode: ((query.viewMode != null) ? getViewModeFromString(query.viewMode) : CalendarViewMode.day),
        timeScale: parseValue<number>(query.timeScale)
    }
};

URI.prototype.setNewLocation = function (options: INewLocation): uri.URI
{
    // Parameter prüfen.
    if (options == null)
    {
        console.error("It has to be set atleast one option!", "parameter: options");
        return null;
    }

    if (options.selectedObject && options.selectedObjectId)
    {
        console.error("Only one selected object should be set!", "parameter: options.selectedObject, options.selectedObjectId");
        return null;
    }

    // URI klonen.
    var uri: uri.URI = this;
    var clone: uri.URI = uri.clone();

    // Gesetzte viewMode ermitteln.
    var currentViewMode: CalendarViewMode = uri.getViewMode();

    // Selektiertes Objekt prüfen und ändern.
    if (options.selectedObject)
    {
        clone.segment(0, options.selectedObject.type);
        clone.segment(1, options.selectedObject.data.id.toString());
    }
    else if (options.selectedObjectId)
    {
        clone.segment(0, options.selectedObjectId.type);
        clone.segment(1, options.selectedObjectId.id.toString());
    }

    // Datum prüfen und setzen.
    if (options.date != null)
    {
        clone.segment(2, options.date.year().toString());
        clone.segment(3, (options.date.isoMonth()).toString());

        if ((options.viewMode != null && options.viewMode !== CalendarViewMode.month) || currentViewMode !== CalendarViewMode.month)
            clone.segment(4, options.date.date().toString());
        else if (clone.segment().length === 5)
            clone.segment(4, null);

    }

    // ViewMode anhängen.
    if (options.viewMode != null)
        clone.setSearch("viewMode", getViewModeString(options.viewMode));

    // Search entfernen, wenn gewollt.
    if (options.replaceSearch)
    {
        let search = uri.search(true);
        for (let src in search)
        {
            if (src.toLowerCase() !== "viewmode")
                clone.removeSearch(src);
        }
    }

    // Search anhängen.
    if (options.search != null)
        clone.setSearch(options.search);

    return clone;
};